import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CheckCircle, AlertCircle, Eye, EyeOff, Loader2, UserCheck, Mail } from 'lucide-react';
import SearchableTimezonePicker from '../SearchableTimezonePicker';

interface OnboardingData {
  onboardingToken: string;
  password: string;
  confirmPassword: string;
  profileData: {
    timezone?: string;
    phone?: string;
    preferences?: {
      emailNotifications: boolean;
      smsNotifications: boolean;
    };
  };
}

const DEFAULT_ONBOARDING: OnboardingData = {
  onboardingToken: '',
  password: '',
  confirmPassword: '',
  profileData: {
    preferences: {
      emailNotifications: true,
      smsNotifications: false
    }
  }
};

const PASSWORD_REQUIREMENTS = [
  { regex: /.{8,}/, message: 'At least 8 characters long' },
  { regex: /[A-Z]/, message: 'Contains uppercase letter' },
  { regex: /[a-z]/, message: 'Contains lowercase letter' },
  { regex: /\d/, message: 'Contains number' },
  { regex: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/, message: 'Contains special character' }
];

export const UserOnboarding: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<OnboardingData>(DEFAULT_ONBOARDING);
  const [currentStep, setCurrentStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isTokenValid, setIsTokenValid] = useState<boolean | null>(null);
  const [userInfo, setUserInfo] = useState<{ firstName: string; lastName: string; email: string } | null>(null);

  // Extract token and email from URL parameters on mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('token');
    const email = params.get('email');
    
    if (token) {
      setFormData(prev => ({ ...prev, onboardingToken: token }));
      
      // If both email and token are provided, validate and jump to password step
      if (email) {
        validateTokenWithEmail(token, email);
      } else {
        validateToken(token);
      }
    } else {
      setError('Invalid onboarding link - missing token');
      setIsTokenValid(false);
    }
  }, [location.search]);

  const validateToken = async (_token: string) => {
    try {
      // We don't have a specific token validation endpoint, 
      // so we'll validate when they submit the form
      setIsTokenValid(true);
      
      // In a real implementation, you might want to fetch user info from the token
      // For now, we'll set placeholder info
      setUserInfo({
        firstName: 'New',
        lastName: 'User',
        email: 'user@example.com'
      });
    } catch (error) {
      setError('Invalid or expired onboarding token');
      setIsTokenValid(false);
    }
  };

  const validateTokenWithEmail = async (token: string, email: string) => {
    try {
      setIsLoading(true);
      setError('');

      // Validate the token and email combination with the backend
      const response = await fetch('/api/onboarding/validate-invitation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: email,
          token: token
        })
      });

      const result = await response.json();

      if (result.success && result.user) {
        setIsTokenValid(true);
        setUserInfo({
          firstName: result.user.first_name || result.user.firstName || 'User',
          lastName: result.user.last_name || result.user.lastName || '',
          email: result.user.email
        });

        // Pre-populate phone number if available from invitation
        if (result.user.phone) {
          setFormData(prev => ({
            ...prev,
            profileData: {
              ...prev.profileData,
              phone: result.user.phone
            }
          }));
        }

        // Jump directly to password creation step (step 1 in our 3-step process)
        setCurrentStep(1);
      } else {
        throw new Error(result.error || 'Invalid invitation link');
      }
    } catch (error: any) {
      console.error('Token validation error:', error);
      setError(`Invalid or expired invitation link: ${error.message}`);
      setIsTokenValid(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: keyof OnboardingData | string, value: any) => {
    if (field.includes('.')) {
      // Handle nested properties like 'profileData.timezone'
      const [parent, child, grandchild] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent as keyof OnboardingData],
          [child]: grandchild ? {
            ...((prev[parent as keyof OnboardingData] as any)?.[child] || {}),
            [grandchild]: value
          } : value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const validatePassword = (password: string) => {
    return PASSWORD_REQUIREMENTS.map(req => ({
      ...req,
      valid: req.regex.test(password)
    }));
  };

  const isPasswordValid = () => {
    return validatePassword(formData.password).every(req => req.valid);
  };

  const isFormValid = () => {
    return formData.password && 
           formData.confirmPassword && 
           formData.password === formData.confirmPassword && 
           isPasswordValid();
  };

  const handleCompleteOnboarding = async () => {
    if (!isFormValid()) {
      setError('Please complete all required fields correctly');
      return;
    }

    try {
      setIsLoading(true);
      setError('');

      const response = await fetch('/api/onboarding/complete-profile', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: userInfo?.email,
          token: formData.onboardingToken,
          password: formData.password,
          firstName: userInfo?.firstName || 'User',
          lastName: userInfo?.lastName || '',
          phone: formData.profileData.phone || '',
          timezone: formData.profileData.timezone || ''
        })
      });

      const result = await response.json();

      if (result.success) {
        setCurrentStep(3); // Success step
        
        // Redirect to login after a brief delay
        setTimeout(() => {
          navigate('/login/client');
        }, 3000);
      } else {
        throw new Error(result.error);
      }
    } catch (error: any) {
      setError(error.message || 'Onboarding failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleNextStep = () => {
    if (currentStep === 1 && isPasswordValid() && formData.password === formData.confirmPassword) {
      setCurrentStep(2);
    }
  };

  const handlePreviousStep = () => {
    if (currentStep === 2) {
      setCurrentStep(1);
    }
  };

  if (isTokenValid === false) {
    return (
      <div className="onboarding-container">
        <div className="onboarding-card error-state">
          <div className="error-icon">
            <AlertCircle size={64} />
          </div>
          <h1>Invalid Onboarding Link</h1>
          <p>This onboarding link is invalid or has expired. Please contact your administrator for a new invitation.</p>
          <button 
            onClick={() => navigate('/login/client')}
            className="return-button"
          >
            Return to Login
          </button>
        </div>
      </div>
    );
  }

  if (isTokenValid === null) {
    return (
      <div className="onboarding-container">
        <div className="onboarding-card">
          <div className="loading-state">
            <Loader2 size={32} className="animate-spin" />
            <p>Validating onboarding link...</p>
          </div>
        </div>
      </div>
    );
  }

  const renderStepIndicator = () => (
    <div className="step-indicator">
      <div className={`step ${currentStep >= 1 ? 'active' : ''} ${currentStep > 1 ? 'completed' : ''}`}>
        <div className="step-number">1</div>
        <div className="step-label">Password</div>
      </div>
      
      <div className="step-connector"></div>
      
      <div className={`step ${currentStep >= 2 ? 'active' : ''} ${currentStep > 2 ? 'completed' : ''}`}>
        <div className="step-number">2</div>
        <div className="step-label">Profile</div>
      </div>
      
      <div className="step-connector"></div>
      
      <div className={`step ${currentStep >= 3 ? 'active' : ''}`}>
        <div className="step-number">3</div>
        <div className="step-label">Complete</div>
      </div>
    </div>
  );

  const renderPasswordStep = () => (
    <div className="step-content">
      <div className="step-header">
        <h2>Create Your Password</h2>
        <p>Choose a strong password to secure your account</p>
      </div>

      <div className="form-group">
        <label className="form-label">Password</label>
        <div className="password-input">
          <input
            type={showPassword ? 'text' : 'password'}
            value={formData.password}
            onChange={(e) => handleInputChange('password', e.target.value)}
            className="form-input"
            placeholder="Enter your password"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="password-toggle"
          >
            {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
          </button>
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">Confirm Password</label>
        <div className="password-input">
          <input
            type={showConfirmPassword ? 'text' : 'password'}
            value={formData.confirmPassword}
            onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
            className="form-input"
            placeholder="Confirm your password"
          />
          <button
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            className="password-toggle"
          >
            {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
          </button>
        </div>
      </div>

      {/* Password Requirements */}
      <div className="password-requirements">
        <h4>Password Requirements:</h4>
        <div className="requirements-list">
          {validatePassword(formData.password).map((req, index) => (
            <div key={index} className={`requirement ${req.valid ? 'valid' : ''}`}>
              <CheckCircle size={16} />
              <span>{req.message}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Password Match Indicator */}
      {formData.confirmPassword && (
        <div className={`password-match ${formData.password === formData.confirmPassword ? 'valid' : 'invalid'}`}>
          {formData.password === formData.confirmPassword ? (
            <>
              <CheckCircle size={16} />
              <span>Passwords match</span>
            </>
          ) : (
            <>
              <AlertCircle size={16} />
              <span>Passwords don't match</span>
            </>
          )}
        </div>
      )}
    </div>
  );

  const renderProfileStep = () => (
    <div className="step-content">
      <div className="step-header">
        <h2>Complete Your Profile</h2>
        <p>Help us personalize your experience</p>
      </div>

      <div className="form-group">
        <label className="form-label">Timezone</label>
        <SearchableTimezonePicker
          id="onboarding-timezone"
          value={formData.profileData.timezone || ''}
          onChange={(value) => handleInputChange('profileData.timezone', value)}
          placeholder="Select your timezone"
          className="form-input"
        />
      </div>

      <div className="form-group">
        <label className="form-label">Phone Number (Optional)</label>
        <input
          type="tel"
          value={formData.profileData.phone || ''}
          onChange={(e) => handleInputChange('profileData.phone', e.target.value)}
          className="form-input"
          placeholder="(555) 123-4567"
        />
      </div>

      <div className="preferences-section">
        <h4>Notification Preferences</h4>
        
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={formData.profileData.preferences?.emailNotifications || false}
            onChange={(e) => handleInputChange('profileData.preferences.emailNotifications', e.target.checked)}
          />
          <span>Email notifications about your progress and appointments</span>
        </label>

        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={formData.profileData.preferences?.smsNotifications || false}
            onChange={(e) => handleInputChange('profileData.preferences.smsNotifications', e.target.checked)}
          />
          <span>SMS reminders (requires phone number)</span>
        </label>
      </div>
    </div>
  );

  const renderSuccessStep = () => (
    <div className="step-content success-content">
      <div className="success-icon">
        <CheckCircle size={64} />
      </div>
      <h2>Welcome to Mission: Trimpossible!</h2>
      <p>Your account has been successfully activated. You can now sign in and start your transformation journey.</p>
      
      <div className="success-actions">
        <button 
          onClick={() => navigate('/login/client')}
          className="sign-in-button"
        >
          <Mail size={20} />
          Sign In to Your Account
        </button>
      </div>
    </div>
  );

  return (
    <div className="onboarding-container">
      <div className="onboarding-card">
        <div className="onboarding-header">
          <h1>Complete Your Setup</h1>
          {userInfo && (
            <p>Welcome, {userInfo.firstName}! Let's get your account ready.</p>
          )}
        </div>

        {renderStepIndicator()}

        <div className="onboarding-form">
          {currentStep === 1 && renderPasswordStep()}
          {currentStep === 2 && renderProfileStep()}
          {currentStep === 3 && renderSuccessStep()}

          {error && (
            <div className="error-message">
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}

          {currentStep < 3 && (
            <div className="form-actions">
              {currentStep > 1 && (
                <button 
                  onClick={handlePreviousStep}
                  className="back-button"
                  disabled={isLoading}
                >
                  Back
                </button>
              )}
              
              {currentStep === 1 && (
                <button 
                  onClick={handleNextStep}
                  disabled={!isPasswordValid() || formData.password !== formData.confirmPassword}
                  className="next-button"
                >
                  Next: Profile Setup
                </button>
              )}
              
              {currentStep === 2 && (
                <button 
                  onClick={handleCompleteOnboarding}
                  disabled={isLoading}
                  className="complete-button"
                >
                  {isLoading ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Activating Account...
                    </>
                  ) : (
                    <>
                      <UserCheck size={16} />
                      Complete Setup
                    </>
                  )}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <style>{`
        .onboarding-container {
          min-height: 100vh;
          background: linear-gradient(135deg, var(--bg-secondary) 0%, var(--bg-primary) 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
        }

        .onboarding-card {
          background: var(--bg-primary);
          border-radius: var(--radius-large);
          box-shadow: var(--shadow-heavy);
          padding: 40px;
          width: 100%;
          max-width: 600px;
          border: 1px solid var(--border-secondary);
        }

        .onboarding-card.error-state {
          text-align: center;
          max-width: 400px;
        }

        .onboarding-header {
          text-align: center;
          margin-bottom: 32px;
        }

        .onboarding-header h1 {
          font-size: 28px;
          font-weight: 700;
          color: var(--text-primary);
          margin-bottom: 8px;
        }

        .onboarding-header p {
          color: var(--text-secondary);
          margin: 0;
        }

        .step-indicator {
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 40px;
        }

        .step {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
        }

        .step-number {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          background: var(--border-secondary);
          color: var(--text-tertiary);
          margin-bottom: 8px;
          transition: all 0.3s ease;
        }

        .step.active .step-number {
          background: var(--accent-blue);
          color: white;
        }

        .step.completed .step-number {
          background: var(--accent-green);
          color: white;
        }

        .step-label {
          font-size: 12px;
          color: var(--text-tertiary);
          font-weight: 500;
        }

        .step.active .step-label {
          color: var(--text-primary);
        }

        .step-connector {
          width: 60px;
          height: 2px;
          background: var(--border-secondary);
          margin: 0 20px 16px;
        }

        .step-content {
          margin-bottom: 32px;
        }

        .step-header {
          text-align: center;
          margin-bottom: 32px;
        }

        .step-header h2 {
          font-size: 24px;
          font-weight: 600;
          color: var(--text-primary);
          margin-bottom: 8px;
        }

        .step-header p {
          color: var(--text-secondary);
          margin: 0;
        }

        .form-group {
          margin-bottom: 24px;
        }

        .form-label {
          display: block;
          font-size: 14px;
          font-weight: 600;
          color: var(--text-primary);
          margin-bottom: 8px;
        }

        .form-input {
          width: 100%;
          padding: 12px 16px;
          border: 1px solid var(--border-secondary);
          border-radius: var(--radius-medium);
          background: var(--bg-primary);
          color: var(--text-primary);
          font-size: 16px;
          transition: all 0.2s ease;
        }

        .form-input:focus {
          outline: none;
          border-color: var(--accent-blue);
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .password-input {
          position: relative;
        }

        .password-toggle {
          position: absolute;
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          color: var(--text-tertiary);
          cursor: pointer;
          padding: 4px;
        }

        .password-toggle:hover {
          color: var(--text-secondary);
        }

        .password-requirements {
          margin-top: 16px;
        }

        .password-requirements h4 {
          font-size: 14px;
          font-weight: 600;
          color: var(--text-primary);
          margin-bottom: 12px;
        }

        .requirements-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .requirement {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
          color: var(--text-tertiary);
        }

        .requirement.valid {
          color: var(--accent-green);
        }

        .requirement svg {
          opacity: 0.5;
        }

        .requirement.valid svg {
          opacity: 1;
        }

        .password-match {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
          margin-top: 12px;
          padding: 8px 12px;
          border-radius: var(--radius-small);
        }

        .password-match.valid {
          color: var(--accent-green);
          background: rgba(34, 197, 94, 0.1);
        }

        .password-match.invalid {
          color: var(--accent-red);
          background: rgba(239, 68, 68, 0.1);
        }

        .preferences-section {
          border-top: 1px solid var(--border-secondary);
          padding-top: 24px;
          margin-top: 24px;
        }

        .preferences-section h4 {
          font-size: 16px;
          font-weight: 600;
          color: var(--text-primary);
          margin-bottom: 16px;
        }

        .checkbox-label {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          margin-bottom: 16px;
          cursor: pointer;
          font-size: 14px;
          color: var(--text-secondary);
          line-height: 1.5;
        }

        .checkbox-label input[type="checkbox"] {
          margin-top: 2px;
        }

        .success-content {
          text-align: center;
        }

        .success-icon {
          margin-bottom: 24px;
          color: var(--accent-green);
        }

        .success-content h2 {
          font-size: 24px;
          font-weight: 600;
          color: var(--text-primary);
          margin-bottom: 16px;
        }

        .success-content p {
          color: var(--text-secondary);
          margin-bottom: 32px;
          line-height: 1.6;
        }

        .sign-in-button {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: var(--accent-blue);
          color: white;
          border: none;
          padding: 16px 24px;
          border-radius: var(--radius-medium);
          font-weight: 600;
          font-size: 16px;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .sign-in-button:hover {
          opacity: 0.9;
          transform: translateY(-1px);
        }

        .error-message {
          display: flex;
          align-items: center;
          gap: 8px;
          color: var(--accent-red);
          background: rgba(239, 68, 68, 0.1);
          border: 1px solid rgba(239, 68, 68, 0.2);
          border-radius: var(--radius-medium);
          padding: 12px 16px;
          margin-bottom: 24px;
          font-size: 14px;
        }

        .form-actions {
          display: flex;
          justify-content: space-between;
          gap: 16px;
        }

        .back-button {
          padding: 12px 24px;
          border: 1px solid var(--border-secondary);
          background: var(--bg-secondary);
          color: var(--text-secondary);
          border-radius: var(--radius-medium);
          cursor: pointer;
          font-weight: 500;
        }

        .back-button:hover:not(:disabled) {
          background: var(--border-secondary);
        }

        .next-button, .complete-button {
          display: flex;
          align-items: center;
          gap: 8px;
          background: var(--accent-blue);
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: var(--radius-medium);
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          margin-left: auto;
        }

        .next-button:hover:not(:disabled),
        .complete-button:hover:not(:disabled) {
          opacity: 0.9;
          transform: translateY(-1px);
        }

        .next-button:disabled,
        .complete-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        .complete-button {
          background: var(--accent-green);
        }

        .error-icon {
          color: var(--accent-red);
          margin-bottom: 24px;
        }

        .error-state h1 {
          font-size: 24px;
          font-weight: 600;
          color: var(--text-primary);
          margin-bottom: 16px;
        }

        .error-state p {
          color: var(--text-secondary);
          margin-bottom: 32px;
          line-height: 1.6;
        }

        .return-button {
          background: var(--accent-blue);
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: var(--radius-medium);
          font-weight: 600;
          cursor: pointer;
        }

        .loading-state {
          text-align: center;
          padding: 40px;
          color: var(--text-secondary);
        }

        .loading-state svg {
          margin-bottom: 16px;
        }

        @media (max-width: 768px) {
          .onboarding-card {
            padding: 24px;
          }
          
          .step-indicator {
            margin-bottom: 32px;
          }
          
          .step-connector {
            width: 40px;
            margin: 0 12px 16px;
          }
          
          .form-actions {
            flex-direction: column;
          }
          
          .next-button, .complete-button {
            margin-left: 0;
          }
        }

        .animate-spin {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </div>
  );
};